import TableView from '@src/components/data-display/tables/TableView';
import { useArIoPrice } from '@src/hooks/useArIOPrice';
import { useDemandFactor } from '@src/hooks/useDemandFactor';
import { camelToReadable, lowerCaseDomain } from '@src/utils';
import {
  ARNS_NAME_REGEX_PARTIAL,
  BASE_NAME_FEES,
  MAX_ARNS_NAME_LENGTH,
} from '@src/utils/constants';
import eventEmitter from '@src/utils/events';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

type TableData = {
  characters: number;
  baseFee: string;
  adjustedRegistrationFee: string;
  renewalFee: string;
  oneYearLeaseFee: string;
  permabuyFee: string;
  undernameFee: string;
  permabuyUndernameFee: string;
} & Record<string, any>;

const columnHelper = createColumnHelper<TableData>();

export default function Prices() {
  const { data: demandFactor, isLoading, error } = useDemandFactor();
  const [domain, setDomain] = useState('');
  const { data: arIoPrice } = useArIoPrice();
  const [unit, setUnit] = useState<'usd' | 'ario'>('ario');

  const formatValue = (
    value: number,
    unit: 'usd' | 'ario',
    arIoPrice: number,
  ) => {
    const converted = unit === 'usd' ? value * arIoPrice : value;
    return `${Number(converted).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${unit.toUpperCase()}`;
  };

  const tableData = useMemo(() => {
    if (!demandFactor || !arIoPrice) return [];
    const newData = Object.entries(BASE_NAME_FEES).map(
      ([characters, baseFee]) => {
        const arf = baseFee * demandFactor;
        const af = arf * 0.2;
        const data: TableData = {
          characters: Number(characters),
          baseFee: formatValue(baseFee, unit, arIoPrice),
          adjustedRegistrationFee: formatValue(arf, unit, arIoPrice),
          renewalFee: formatValue(af, unit, arIoPrice),
          oneYearLeaseFee: formatValue(arf + af * 1, unit, arIoPrice),
          permabuyFee: formatValue(arf + af * 20, unit, arIoPrice),
          undernameFee: formatValue(
            baseFee * demandFactor * 0.001,
            unit,
            arIoPrice,
          ),
          permabuyUndernameFee: formatValue(
            baseFee * demandFactor * 0.005,
            unit,
            arIoPrice,
          ),
        };
        return data;
      },
    );
    return newData;
  }, [demandFactor, isLoading, error, arIoPrice, unit]);

  const columns = [
    'characters',
    'oneYearLeaseFee',
    'renewalFee',
    'permabuyFee',
    'undernameFee',
    'permabuyUndernameFee',
  ].map((key) => {
    return columnHelper.accessor(key, {
      id: key,
      header: () => {
        return <span className="">{camelToReadable(key)} </span>;
      },
      cell: ({ row }) => {
        return row.getValue(key);
      },
    });
  });

  return (
    <div className="page sm:px-10 lg:px-[100px]">
      <div className="flex justify-between w-full items-center gap-5">
        <h1 className="text-white text-3xl w-full">
          Prices (${unit.toUpperCase()})
        </h1>{' '}
        <div className="flex items-center gap-2">
          <button
            className={
              (unit === 'usd'
                ? 'bg-primary text-black'
                : ' bg-dark-grey  text-light-grey') +
              ` flex px-3 py-2 rounded  hover:bg-primary-thin hover:text-primary transition-all`
            }
            onClick={() => setUnit('usd')}
            type="button"
          >
            $USD
          </button>
          <button
            className={
              (unit === 'ario'
                ? 'bg-primary text-black'
                : ' bg-dark-grey  text-light-grey') +
              ` flex px-3 py-2 rounded  hover:bg-primary-thin hover:text-primary transition-all`
            }
            onClick={() => setUnit('ario')}
            type="button"
          >
            $ARIO
          </button>
        </div>
        <span className="flex p-2 bg-foreground border border-dark-grey rounded text-white whitespace-nowrap">
          Demand Factor:{' '}
          {isLoading ? 'Loading...' : error ? error.message : demandFactor}
        </span>
      </div>

      <div className="flex w-full py-2 justify-between  items-center gap-5">
        <input
          className="flex w-full p-2 bg-background border-b border-dark-grey outline-none text-white"
          placeholder="Enter an ArNS Name to see its fee"
          value={domain}
          onChange={(e) => {
            const trimmed = e.target.value.trim();
            if (trimmed.length > MAX_ARNS_NAME_LENGTH) return;
            if (!ARNS_NAME_REGEX_PARTIAL.test(lowerCaseDomain(trimmed))) {
              eventEmitter.emit('error', {
                name: 'Invalid ARNS name',
                message: `${trimmed} is not a valid arns name`,
              });
              return;
            }
            setDomain(trimmed);
          }}
        />
      </div>

      <div className="flex flex-col size-full gap-2">
        <TableView
          data={tableData}
          columns={columns}
          isLoading={false}
          defaultSortingState={{ id: 'characters', desc: false }}
          tableClass="border-[1px] border-dark-grey relative"
          paginationConfig={{ pageSize: 51 }}
          headerClass="sticky absolute"
          bodyClass="scrollbar overflow-y-scroll"
          rowClass={(props) => {
            const rowCharLength = props?.row?.getValue('characters');
            if (props?.row !== undefined) {
              return rowCharLength === lowerCaseDomain(domain).length
                ? 'bg-primary-thin border-l-2 border-primary border-t-0'
                : '';
            }

            return '';
          }}
          dataClass={(props) => {
            if (props?.row !== undefined && props.row.getIsExpanded()) {
              return 'border-t-[1px] border-dark-grey border-b-0';
            }

            return '';
          }}
        />
      </div>
    </div>
  );
}
